/**
 * LmsMyQuizzes — Flat list of all assigned quizzes (pending + completed).
 * Feb 2026 R11.
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { HelpCircle, Clock, Lock, CheckCircle2, XCircle, PlayCircle, ShieldAlert } from "lucide-react";

export default function LmsMyQuizzes() {
  const { activeWorkspaceId } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({ pending: [], completed: [], counts: { pending: 0, completed: 0 } });
  const [tab, setTab] = useState("pending");

  const load = useCallback(async () => {
    if (!activeWorkspaceId) return;
    try {
      const { data: d } = await api.get(`/workspaces/${activeWorkspaceId}/lms/me/quizzes`);
      setData(d);
    } catch (e) { toast.error(apiErrorMessage(e, "Load failed")); }
  }, [activeWorkspaceId]);
  useEffect(() => { load(); }, [load]);

  const rows = tab === "pending" ? data.pending : data.completed;

  return (
    <div className="p-6 space-y-4">
      <PageHeader title="My Quizzes" description="Every quiz assigned to you — take pending, review completed" />
      <div className="border-b border-border flex gap-1">
        {[["pending", `Pending (${data.counts.pending})`], ["completed", `Completed (${data.counts.completed})`]].map(([v, l]) => (
          <button key={v} onClick={() => setTab(v)} className={`px-3 py-1.5 text-xs uppercase tracking-widest ${tab === v ? "border-b-2 border-primary text-primary" : "text-muted-foreground"}`} data-testid={`myq-tab-${v}`}>{l}</button>
        ))}
      </div>
      {rows.length === 0 && (
        <div className="border border-dashed border-border p-8 text-center">
          <HelpCircle className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">{tab === "pending" ? "No pending quizzes." : "No completed quizzes yet."}</p>
        </div>
      )}
      <div className="space-y-2">
        {rows.map((q) => (
          <div key={q.quiz_id} className="border border-border p-3 flex items-center gap-3" data-testid={`myq-item-${q.quiz_id}`}>
            <HelpCircle className="w-5 h-5 text-amber-500 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm truncate">{q.title}</div>
              <div className="text-[10px] text-muted-foreground mt-0.5 flex gap-2 flex-wrap">
                {q.course_title && <span>Course: {q.course_title}</span>}
                <span className="chip chip-info text-[9px]">{q.prometric_mode}</span>
                {q.time_limit_min && <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{q.time_limit_min}m</span>}
                <span>Pass: {q.pass_percent}%</span>
                {q.unlock_at && !q.is_locked && <span>Unlocked</span>}
                {q.close_at && <span>Closes {q.close_at.split("T")[0]}</span>}
                <span>{q.attempt_count}/{q.max_attempts} attempts</span>
              </div>
              {q.is_locked && q.lock_reason && (
                <div className="text-[11px] text-amber-600 mt-0.5 flex items-center gap-1"><Lock className="w-3 h-3" /> {q.lock_reason}</div>
              )}
            </div>
            {tab === "completed" ? (
              <div className="text-right">
                {q.latest_pass ? <CheckCircle2 className="w-5 h-5 text-emerald-500 inline" /> : <XCircle className="w-5 h-5 text-rose-500 inline" />}
                <div className="text-lg font-mono">{q.latest_score_percent}%</div>
                <div className="text-[9px] text-muted-foreground">{q.latest_submitted_at?.split("T")[0]}</div>
              </div>
            ) : (
              <Button
                onClick={() => navigate(`/app/lms/quizzes/${q.quiz_id}/take${q.enrollment_id ? `?enrollment_id=${q.enrollment_id}` : ""}`)}
                disabled={q.is_locked}
                className="rounded-none"
                data-testid={`myq-take-${q.quiz_id}`}
              >
                <PlayCircle className="w-4 h-4 mr-1" /> {q.is_locked ? "Locked" : "Take"}
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
